import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import styles from './ConfirmDialog.module.css';

/**
 * Подтверждение необратимого действия.
 *
 * Роль `alertdialog`, а не `dialog`: окно требует ответа здесь и сейчас, и скринридер должен
 * прочитать вопрос сразу, не дожидаясь, пока до него дойдут табуляцией. Фокус при открытии
 * встаёт на отмену — случайный Enter не должен ничего удалять.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  error,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  /** Код ошибки прошлой попытки: окно остаётся открытым и показывает, что пошло не так. */
  error?: string | null;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.ReactElement {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const { t } = useTranslation();

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.dialog}
          role="alertdialog"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            cancelRef.current?.focus();
          }}
        >
          <Dialog.Title className={styles.title}>{title}</Dialog.Title>
          <Dialog.Description className={styles.body}>{body}</Dialog.Description>

          {error && <p className={styles.error}>{t(`errors.${error}`)}</p>}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancel}
              ref={cancelRef}
              onClick={onCancel}
              disabled={busy}
            >
              {t('confirm.cancel')}
            </button>
            <button type="button" className={styles.danger} onClick={onConfirm} disabled={busy}>
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
